import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { logWarn } from "@/lib/log";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, object = authed, false = anonymous
  const [user, setUser] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      logWarn("Logout request failed; clearing local session anyway", e);
    }
    setUser(false);
  }, []);

  const forgotPassword = useCallback((email) => api.post("/auth/forgot-password", { email }), []);

  const validateResetToken = useCallback((token) => api.get(`/auth/reset-token/${token}`), []);

  const resetPassword = useCallback(async (token, password, extra = {}) => {
    const { data } = await api.post("/auth/reset-password", { token, password, ...extra });
    setUser(data);
    return data;
  }, []);

  const changePassword = useCallback(
    (current_password, new_password) => api.post("/auth/change-password", { current_password, new_password }),
    []
  );

  const value = useMemo(
    () => ({ user, login, logout, forgotPassword, validateResetToken, resetPassword, changePassword, formatApiError, refresh: checkAuth }),
    [user, login, logout, forgotPassword, validateResetToken, resetPassword, changePassword, checkAuth]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
